import React, { useState } from "react";
import { toast } from "react-toastify";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { useAppDispatch } from "@/store";
import { shapeOfYouActions } from "@/store/slices/shapeOfYou";
import { PhotoCaptureFlowProps, PhotoCaptureStep, MobileStep } from "./types";
import UploadOrScanScreen from "./UploadOrScanScreen";
import GalleryPermissionScreen from "./GalleryPermissionScreen";
import CameraPermissionScreen from "./CameraPermissionScreen";
import FaceScanningScreen from "./FaceScanningScreen";
import PermissionDeniedScreen from "./PermissionDeniedScreen";
import FaceResultScreen from "./FaceResultScreen";
import ImageNotPermittedScreen from "./ImageNotPermittedScreen";

const PhotoCaptureFlow: React.FC<PhotoCaptureFlowProps> = ({
  initialStep,
  isAuthenticated,
  onComplete,
  onExit,
  logStep,
}) => {
  const dispatch = useAppDispatch();

  // Internal state management
  const [currentStep, setCurrentStep] = useState<PhotoCaptureStep>(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [captureMethod, setCaptureMethod] = useState<"upload" | "scan">("upload");
  const [faceData, setFaceData] = useState({
    faceShape: "",
    complexion: "",
    undertone: "",
  });

  // Internal navigation - accepts MobileStep for compatibility but only handles PhotoCaptureStep
  const navigateToStep = (step: MobileStep) => {
    const validSteps: MobileStep[] = [
      "uploadOrScan",
      "galleryPermission",
      "cameraPermission",
      "faceScanning",
      "permissionDenied",
      "imageNotPermitted",
      "faceResult",
    ];

    if (validSteps.includes(step)) {
      setCurrentStep(step as PhotoCaptureStep);
    } else {
      console.warn(
        `PhotoCaptureFlow: Invalid step "${step}" - ignoring navigation`
      );
    }
  };

  // Send image to API (following FaceScan pattern)
  const sendImageToAPI = async (base64Image: string, method: "upload" | "scan" = "upload") => {
    setIsLoading(true);
    const requestPayload = { image_data: base64Image };
    const apiEndpoint = endpoints.shape_of_you.profile();

    try {
      const { data } = await httpClient.post(apiEndpoint, requestPayload);

      console.log("API Response:", data);

      // Log successful face scan with API response (non-blocking)
      if (logStep) {
        logStep("face_scan_uploaded", {
          api_endpoint: apiEndpoint,
          request_payload: { image_data: "[base64...]" }, // Sanitized
          response_status: 200,
          response_payload: {
            face_shape: data.face_shape,
            tone: data.tone,
            undertone: data.undertone,
          },
        });
      }

      // Store in Redux
      dispatch(shapeOfYouActions.setCapturedImage(base64Image));

      // Extract and store response data
      setCapturedImage(base64Image);
      setCaptureMethod(method);
      setFaceData({
        faceShape: data.face_shape?.label || "",
        complexion: data?.tone || "",
        undertone: data?.undertone || "",
      });

      // Navigate to result screen
      navigateToStep("faceResult");
    } catch (error: any) {
      console.error("Error sending image to API:", error);

      // Log API error (non-blocking)
      if (logStep) {
        logStep("face_scan_uploaded_failed", {
          api_endpoint: apiEndpoint,
          request_payload: { image_data: "[base64...]" }, // Sanitized
          response_status: error.response?.status || 500,
          error_message: error.response?.data?.message || "Failed to analyze image",
          error_code: error.response?.status?.toString() || "500",
        });
      }

      setCapturedImage(base64Image);
      setCaptureMethod(method);
      navigateToStep("imageNotPermitted");
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper for upload method
  const handleUploadImage = async (base64Image: string) => {
    await sendImageToAPI(base64Image, "upload");
  };

  // Wrapper for scan method
  const handleScanImage = async (base64Image: string) => {
    await sendImageToAPI(base64Image, "scan");
  };

  // Handle reupload - reset state and go back to start
  const handleReupload = () => {
    setCapturedImage(null);
    setCaptureMethod("upload");
    setFaceData({
      faceShape: "",
      complexion: "",
      undertone: "",
    });
    navigateToStep("uploadOrScan");
  };

  // Handle explore jewellery - fetch recommendations then call parent onComplete
  const handleExploreJewellery = async () => {
    if (!faceData.faceShape || !capturedImage) {
      toast.error("No data available");
      return;
    }

    setIsLoading(true);
    try {
      // Call the recommendations API (following FaceScan.tsx pattern)
      const { data } = await httpClient.post(endpoints.shape_of_you.run(), {
        complexion: faceData.complexion,
        face_shape: faceData.faceShape,
        tone: faceData.undertone,
      });

      console.log("Recommendations API Response:", data);

      // Store recommendations in Redux
      dispatch(shapeOfYouActions.setRecommendations(data));

      // Call parent onComplete to navigate
      onComplete({
        faceShape: faceData.faceShape,
        complexion: faceData.complexion,
        undertone: faceData.undertone,
        capturedImage,
      });
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      toast.error("Failed to get recommendations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Upload Or Scan Screen */}
      {currentStep === "uploadOrScan" && (
        <UploadOrScanScreen
          navigateToStep={navigateToStep}
          onImageSelect={handleUploadImage}
        />
      )}

      {/* Gallery Permission Screen */}
      {currentStep === "galleryPermission" && (
        <GalleryPermissionScreen
          navigateToStep={navigateToStep}
          onImageSelect={handleUploadImage}
          logStep={logStep}
        />
      )}

      {/* Camera Permission Screen */}
      {currentStep === "cameraPermission" && (
        <CameraPermissionScreen navigateToStep={navigateToStep} logStep={logStep} />
      )}

      {/* Face Scanning Screen */}
      {currentStep === "faceScanning" && (
        <FaceScanningScreen
          navigateToStep={navigateToStep}
          onCapture={handleScanImage}
        />
      )}

      {/* Permission Denied Screen */}
      {currentStep === "permissionDenied" && (
        <PermissionDeniedScreen
          navigateToStep={navigateToStep}
          isAuthenticated={isAuthenticated}
          onExit={onExit}
        />
      )}

      {/* Face Result Screen */}
      {currentStep === "faceResult" && (
        <FaceResultScreen
          navigateToStep={navigateToStep}
          isAuthenticated={isAuthenticated}
          faceShape={faceData.faceShape}
          complexion={faceData.complexion}
          capturedImage={capturedImage}
          captureMethod={captureMethod}
          onReupload={handleReupload}
          onExploreJewellery={handleExploreJewellery}
        />
      )}

      {/* Image Not Permitted Screen */}
      {currentStep === "imageNotPermitted" && (
        <ImageNotPermittedScreen
          navigateToStep={navigateToStep}
          errorMessage="No face detected in the image. Please try again with a clear photo showing your face."
          onRetry={handleReupload}
          capturedImage={capturedImage}
        />
      )}

      {/* API Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: "center", color: "#fff" }}>
            <div
              className="spinner-border text-light mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Analyzing image...</span>
            </div>
            <p style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              Analyzing your image...
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoCaptureFlow;
